import {
  type DataSelector,
  type FileSelector,
  type InvocationInput,
  selectorsOf,
} from "@/shared/domain/actions";

/** Which kind of record a selector picks; it sets the label, icon and links a line is drawn with. */
export type InputScope = "session" | "file" | "topic";

/** One selector field, in the shape the invocations cell draws it. */
export type InputLine =
  | { kind: "ids"; scope: InputScope; ids: string[] }
  | { kind: "names"; scope: InputScope; names: string[] }
  | { kind: "query"; scope: InputScope; query: string }
  /** A file selector's dataset. `pathCount` paths narrow it; null means all of it. */
  | { kind: "dataset"; datasetId: string; pathCount: number | null };

export interface InputSummary {
  /** The first line, or null when the invocation selected nothing. */
  line: InputLine | null;
  /** One line of text per selector field, the lead one included, for the overflow tooltip. */
  selectors: string[];
}

/** The glob a dataset-wide file selector carries; it narrows nothing, so it isn't a path count. */
const ALL_FILES_GLOB = "**/*";

/**
 * What an invocation ran on, reduced to one line for the invocations list.
 *
 * Sessions lead, then files, then topics: a session is the narrowest thing a reader names, and
 * a topic selector usually rides along with the files it came from. Every selector field is a
 * line of its own, so a selector that sets several (ids and a query, say) loses none of them:
 * the first becomes the row's line and the rest are described in `selectors`, which the row
 * counts as "+N more".
 */
export function summarizeInvocationInput(input: InvocationInput): InputSummary {
  const lines = [
    ...selectorsOf(input.sessions).flatMap((s) => linesOf("session", s)),
    ...selectorsOf(input.files).flatMap((s) => linesOf("file", s)),
    ...selectorsOf(input.topics).flatMap((s) => linesOf("topic", s)),
  ];
  return { line: lines[0] ?? null, selectors: lines.map(describe) };
}

/**
 * A query split before each ` AND ` / ` OR `, so a tooltip can set one clause per line.
 *
 * Presentation only: a keyword inside a quoted value splits too, which costs a line break, not
 * meaning.
 */
export function queryClauses(query: string): string[] {
  return query.split(/ (?=(?:AND|OR) )/);
}

/**
 * One line per field the selector sets, the ones that name records before the query. Every
 * scope reads the same fields the same way, as the action runtime resolves them: ids, names
 * and a query each pick their own records, and the results add up.
 *
 * A selector's `dataset_id` matters only to a file selector's `paths` (the runtime ignores it
 * for every other field, with a warning), so that is the only place it is drawn: a dataset
 * line for the paths, or for the whole dataset when the selector sets nothing else.
 */
function linesOf(
  scope: InputScope,
  selector: DataSelector | FileSelector,
): InputLine[] {
  const lines: InputLine[] = [];
  if (selector.ids?.length) {
    lines.push({ kind: "ids", scope, ids: selector.ids });
  }
  if (selector.names?.length) {
    lines.push({ kind: "names", scope, names: selector.names });
  }
  const paths = "paths" in selector ? (selector.paths ?? []) : [];
  const onlyDataset =
    !selector.ids?.length && !selector.names?.length && !selector.query;
  if (
    scope === "file" &&
    selector.dataset_id &&
    (paths.length > 0 || onlyDataset)
  ) {
    const narrowing = paths.filter((path) => path !== ALL_FILES_GLOB);
    lines.push({
      kind: "dataset",
      datasetId: selector.dataset_id,
      pathCount: narrowing.length > 0 ? narrowing.length : null,
    });
  }
  if (selector.query) {
    lines.push({ kind: "query", scope, query: selector.query });
  }
  return lines;
}

const SCOPE_LABELS: Record<InputScope, string> = {
  session: "Sessions",
  file: "Files",
  topic: "Topics",
};

function describe(line: InputLine): string {
  switch (line.kind) {
    case "ids":
      return `${SCOPE_LABELS[line.scope]}: ${line.ids.join(", ")}`;
    case "names":
      return `${SCOPE_LABELS[line.scope]}: ${line.names.join(", ")}`;
    case "query":
      return `${SCOPE_LABELS[line.scope]}: ${line.query}`;
    case "dataset":
      return line.pathCount === null
        ? `Files: ${line.datasetId}`
        : `Files: ${line.datasetId} · ${plural(line.pathCount, "path")}`;
  }
}

export function plural(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? "" : "s"}`;
}
